import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';

const isSmsGatewayConnected = (gateway: { isEnabled?: boolean; status?: string; connectionId?: string | null }) => (
  !!gateway.isEnabled
  && String(gateway.status || '').toLowerCase() === 'online'
  && !!gateway.connectionId
);

export const UsageDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalAlerts: 0,
    totalSms: 0,
    totalEmail: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [platformSmsAlert, setPlatformSmsAlert] = useState<string | null>(null);
  const [tenantSmsAlerts, setTenantSmsAlerts] = useState<string[]>([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { tenants } = await adminApi.getTenants({ take: 1000 });
      const gateways = await adminApi.getPlatformSmsGateways();
      let totalAlerts = 0;
      let totalSms = 0;
      let totalEmail = 0;
      let totalUsers = 0;
      const tenantGatewayMap = new Map<string, boolean>();

      gateways.forEach((gateway) => {
        if (!gateway.tenantId) {
          return;
        }

        const existing = tenantGatewayMap.get(gateway.tenantId) || false;
        tenantGatewayMap.set(gateway.tenantId, existing || isSmsGatewayConnected(gateway));
      });

      setPlatformSmsAlert(
        gateways.some((gateway) => !gateway.tenantId && isSmsGatewayConnected(gateway))
          ? null
          : 'No active, connected platform SMS gateway is available. Platform fallback SMS delivery is currently unavailable.'
      );

      const affectedTenants: string[] = [];

      for (const tenant of tenants) {
        try {
          const detail = await adminApi.getTenant(tenant.tenantId);
          if (detail.usage) {
            totalAlerts += detail.usage.totalAlerts;
            totalSms += detail.usage.smsCount;
            totalEmail += detail.usage.emailCount;
          }
          totalUsers += detail.adminUsers?.length || 0;

          const alertMode = detail.smsGatewayAlertMode || 'TenantAndSuperAdminDashboard';
          if (alertMode === 'TenantAndSuperAdminDashboard' && !(tenantGatewayMap.get(tenant.tenantId) || false)) {
            affectedTenants.push(detail.name);
          }
        } catch (err) {
          console.error(`Failed to load tenant ${tenant.tenantId}:`, err);
        }
      }

      setTenantSmsAlerts(affectedTenants);

      setStats({
        totalTenants: tenants.length,
        activeTenants: tenants.filter(t => t.status === 'Active').length,
        totalAlerts,
        totalSms,
        totalEmail,
        totalUsers,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading usage data...</div>;
  }

  return (
    <div className="management-section">
      <h2>Platform Usage Dashboard</h2>
      {platformSmsAlert && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{platformSmsAlert}</div>}
      {tenantSmsAlerts.length > 0 && (
        <div className="error-message" style={{ marginBottom: '0.75rem' }}>
          Tenant SMS gateway outage detected for: {tenantSmsAlerts.join(', ')}.
        </div>
      )}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Tenants</h3>
          <p className="stat-value">{stats.totalTenants}</p>
          <p className="stat-detail">{stats.activeTenants} active</p>
        </div>
        <div className="stat-card">
          <h3>Total Alerts (30d)</h3>
          <p className="stat-value">{stats.totalAlerts.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <h3>SMS Sent (30d)</h3>
          <p className="stat-value">{stats.totalSms.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <h3>Email Sent (30d)</h3>
          <p className="stat-value">{stats.totalEmail.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <h3>Total Users</h3>
          <p className="stat-value">{stats.totalUsers}</p>
        </div>
      </div>
    </div>
  );
};
